import React, { useState, useEffect, useRef } from 'react';
import { Table, Input, Button } from '@mui/joy';
import { customerService } from '../api/customer';
import { Customer, CustomerCreateDTO } from '../api/customer';
import { Edit, Trash, Plus } from 'lucide-react';
import { PAGE_TRANSLATIONS } from '../conf';
import ConfirmDialog from '../components/ConfirmDialog';

const CustomerPage: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [newCustomer, setNewCustomer] = useState<CustomerCreateDTO>({ name: '', telephone: '', address: '' });
  const [nameError, setNameError] = useState(false);
  const fetchInitiatedRef = useRef(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [customerIdToDelete, setCustomerIdToDelete] = useState<number | null>(null);

  useEffect(() => {
    if (fetchInitiatedRef.current) {
      return;
    }
    fetchInitiatedRef.current = true;

    const fetchCustomers = async () => {
      try {
        const response = await customerService.list({ sort: 'id', order: 'ASC' });
        setCustomers(response.data.data);
      } catch (error) {
        console.error('Failed to fetch customers', error);
      }
    };
    fetchCustomers();
  }, []);

  const handleSave = async (customer: Customer | CustomerCreateDTO) => {
    // Validate required fields
    if (!customer.name.trim()) {
      if ('id' in customer) {
        setEditingCustomer({ ...customer, name: '' });
      } else {
        setNameError(true);
      }
      return; // Stop the save operation
    }

    try {
      if ('id' in customer) {
        await customerService.update(customer.id, customer);
        setCustomers(customers.map(c => (c.id === customer.id ? customer : c)));
      } else {
        const response = await customerService.create(customer);
        setCustomers([...customers, response.data.data]);
        setNewCustomer({ name: '', telephone: '', address: '' });
        setNameError(false);
      }
      setEditingCustomer(null);
    } catch (error) {
      console.error('Failed to save customer', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewCustomer({ ...newCustomer, [name]: value });
    
    // Clear error when user starts typing in the required field
    if (name === 'name' && value.trim()) {
      setNameError(false);
    }
  };

  const requestDelete = (id: number) => {
    setCustomerIdToDelete(id);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (customerIdToDelete !== null) {
      try {
        await customerService.delete(customerIdToDelete);
        setCustomers(customers.filter(c => c.id !== customerIdToDelete));
      } catch (error) {
        console.error('Failed to delete customer', error);
      }
    }
    setConfirmOpen(false);
    setCustomerIdToDelete(null);
  };

  return (
    <div>
      <h2>{PAGE_TRANSLATIONS.CUSTOMERS}</h2>
      <Table>
        <thead>
          <tr>
            <th>{PAGE_TRANSLATIONS.NAME}</th>
            <th>{PAGE_TRANSLATIONS.TELEPHONE}</th>
            <th>{PAGE_TRANSLATIONS.ADDRESS}</th>
            <th>{PAGE_TRANSLATIONS.ACTIONS}</th>
          </tr>
        </thead>
        <tbody>
          {customers.map(customer => (
            <tr key={customer.id}>
              <td>
                {editingCustomer?.id === customer.id ? (
                  <Input 
                    value={editingCustomer.name} 
                    onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })} 
                    error={editingCustomer.name.trim() === ''}
                  />
                ) : (
                  customer.name
                )}
              </td>
              <td>
                {editingCustomer?.id === customer.id ? (
                  <Input value={editingCustomer.telephone} onChange={(e) => setEditingCustomer({ ...editingCustomer, telephone: e.target.value })} />
                ) : (
                  customer.telephone
                )}
              </td>
              <td>
                {editingCustomer?.id === customer.id ? (
                  <Input value={editingCustomer.address} onChange={(e) => setEditingCustomer({ ...editingCustomer, address: e.target.value })} />
                ) : (
                  customer.address
                )}
              </td>
              <td>
                {editingCustomer?.id === customer.id ? (
                  <>
                    <Button variant="plain" onClick={() => handleSave(editingCustomer)}>{PAGE_TRANSLATIONS.SAVE}</Button>
                    <Button variant="plain" onClick={() => setEditingCustomer(null)}>{PAGE_TRANSLATIONS.CANCEL}</Button>
                  </>
                ) : (
                  <>
                    <Button variant="plain" onClick={() => setEditingCustomer(customer)}><Edit size={16} /></Button>
                    <Button variant="plain" onClick={() => requestDelete(customer.id)}><Trash size={16} /></Button>
                  </>
                )}
              </td>
            </tr>
          ))}
          <tr>
            <td>
              <Input 
                placeholder={`${PAGE_TRANSLATIONS.NAME} *`} 
                value={newCustomer.name} 
                onChange={(e) => handleChange({ ...e, target: { ...e.target, name: 'name' } })}
                error={nameError}
                name="name"
              />
            </td>
            <td>
              <Input 
                placeholder={PAGE_TRANSLATIONS.TELEPHONE} 
                value={newCustomer.telephone} 
                onChange={(e) => handleChange({ ...e, target: { ...e.target, name: 'telephone' } })}
                name="telephone"
              />
            </td>
            <td>
              <Input 
                placeholder={PAGE_TRANSLATIONS.ADDRESS} 
                value={newCustomer.address} 
                onChange={(e) => handleChange({ ...e, target: { ...e.target, name: 'address' } })}
                name="address"
              />
            </td>
            <td><Button onClick={() => handleSave(newCustomer)}><Plus size={16} /></Button></td>
          </tr>
        </tbody>
      </Table>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message={PAGE_TRANSLATIONS.DELETE_CONFIRM_CUSTOMER}
        confirmText="Delete"
        cancelText={PAGE_TRANSLATIONS.CANCEL}
      />
    </div>
  );
};

export default CustomerPage; 